import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, X, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { galleryPhotos } from "@/data/gallery";

const spanCls = {
  wide: "md:col-span-2 aspect-[4/3] md:aspect-[2/1]",
  tall: "md:row-span-2 aspect-[4/3] md:aspect-auto",
  square: "aspect-[4/3] md:aspect-square",
};

function Placeholder({ caption }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-secondary/40" data-testid="gallery-placeholder">
      <img src="/assets/osh-logo.jpeg" alt="" aria-hidden="true" className="h-16 md:h-20 w-auto object-contain opacity-30 grayscale group-hover:opacity-60 transition-opacity duration-500" />
      <span className="inline-flex items-center gap-2 border border-gold/40 text-gold/80 font-display uppercase tracking-[0.25em] text-[10px] md:text-xs px-3 py-1.5">
        <Plus className="w-3 h-3" /> Photo Coming Soon
      </span>
      <span className="sr-only">{caption}</span>
    </div>
  );
}

function VideoTile({ photo, onOpen, index }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(index)}
      aria-label={`Play video: ${photo.caption}`}
      data-testid={`gallery-open-${photo.id}`}
      className="absolute inset-0 w-full h-full text-left cursor-zoom-in"
    >
      <video
        src={photo.src}
        poster={photo.poster}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        aria-label={photo.alt}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        data-testid={`gallery-video-${photo.id}`}
      />
      <span className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-500" aria-hidden="true" />
      <span className="absolute top-5 left-5 inline-flex items-center gap-2 bg-gold text-black font-display uppercase tracking-[0.25em] text-[10px] px-2.5 py-1" data-testid={`gallery-video-badge-${photo.id}`}>
        <span className="w-1.5 h-1.5 rounded-full bg-black animate-pulse" aria-hidden="true" /> Live Look
      </span>
    </button>
  );
}

function Tile({ photo, index, onOpen }) {
  const filled = Boolean(photo.src);
  const isVideo = photo.type === "video";
  return (
    <motion.figure
      initial={{ opacity: 0, y: 40, clipPath: "inset(12% 0 12% 0)" }}
      whileInView={{ opacity: 1, y: 0, clipPath: "inset(0 0 0 0)" }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: (index % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative overflow-hidden border border-white/10 hover:border-gold/60 transition-colors duration-500 ${spanCls[photo.span]}`}
      data-testid={`gallery-tile-${photo.id}`}
    >
      {filled && isVideo ? (
        <VideoTile photo={photo} index={index} onOpen={onOpen} />
      ) : filled ? (
        <button
          type="button"
          onClick={() => onOpen(index)}
          aria-label={`Open photo: ${photo.caption}`}
          data-testid={`gallery-open-${photo.id}`}
          className="absolute inset-0 w-full h-full text-left cursor-zoom-in"
        >
          <img src={photo.src} alt={photo.alt} loading="lazy" style={{ objectPosition: photo.position }} className="w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
          <span className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-500" aria-hidden="true" />
        </button>
      ) : (
        <Placeholder caption={photo.caption} />
      )}
      <figcaption className="pointer-events-none absolute left-5 bottom-5 flex items-center gap-3">
        <span className="font-display font-bold text-stroke-gold text-2xl select-none" aria-hidden="true">{String(photo.id).padStart(2, "0")}</span>
        <span className="font-display uppercase tracking-[0.2em] text-xs text-white translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500" data-testid={`gallery-caption-${photo.id}`}>
          {photo.caption}
        </span>
      </figcaption>
    </motion.figure>
  );
}

function Lightbox({ photos, index, onClose, onStep }) {
  const photo = photos[index];
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onStep(1);
      if (e.key === "ArrowLeft") onStep(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onStep]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] bg-black/95 backdrop-blur-xl flex items-center justify-center p-5 md:p-12"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={photo.caption}
      data-testid="gallery-lightbox"
    >
      <button onClick={onClose} aria-label="Close gallery" data-testid="gallery-lightbox-close" className="absolute top-5 right-5 md:top-8 md:right-8 text-zinc-400 hover:text-gold transition-colors">
        <X className="w-7 h-7" />
      </button>
      <button onClick={(e) => { e.stopPropagation(); onStep(-1); }} aria-label="Previous photo" data-testid="gallery-lightbox-prev" className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-gold transition-colors">
        <ChevronLeft className="w-9 h-9" />
      </button>
      <button onClick={(e) => { e.stopPropagation(); onStep(1); }} aria-label="Next photo" data-testid="gallery-lightbox-next" className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-gold transition-colors">
        <ChevronRight className="w-9 h-9" />
      </button>
      <motion.figure
        key={photo.id}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="max-w-6xl w-full"
      >
        {photo.type === "video" ? (
          <video src={photo.src} poster={photo.poster} controls autoPlay muted loop playsInline aria-label={photo.alt} className="w-full max-h-[78vh] object-contain border border-white/10 bg-black" data-testid="gallery-lightbox-video" />
        ) : (
          <img src={photo.src} alt={photo.alt} className="w-full max-h-[78vh] object-contain border border-white/10" data-testid="gallery-lightbox-image" />
        )}
        <figcaption className="mt-4 flex items-center justify-between gap-4">
          <span className="font-display uppercase tracking-[0.25em] text-xs text-gold" data-testid="gallery-lightbox-caption">{photo.caption}</span>
          <span className="font-display text-xs text-zinc-500 tracking-[0.2em]">{String(index + 1).padStart(2, "0")} / {String(photos.length).padStart(2, "0")}</span>
        </figcaption>
      </motion.figure>
    </motion.div>
  );
}

export default function Gallery() {
  const filled = galleryPhotos.filter((p) => p.src);
  const [open, setOpen] = useState(null);

  const openAt = useCallback((tileIndex) => {
    const id = galleryPhotos[tileIndex].id;
    setOpen(filled.findIndex((p) => p.id === id));
  }, [filled]);
  const close = useCallback(() => setOpen(null), []);
  const step = useCallback((d) => setOpen((i) => (i + d + filled.length) % filled.length), [filled.length]);

  return (
    <section id="gallery" className="py-24 md:py-40 border-b border-white/10" data-testid="gallery-section">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-14 md:mb-20 flex flex-col md:flex-row md:items-end justify-between gap-6"
        >
          <div>
            <p className="font-display uppercase tracking-[0.3em] text-xs text-gold mb-5 inline-flex items-center gap-2">
              <Camera className="w-4 h-4" /> Inside The Gym
            </p>
            <h2 className="font-display font-bold uppercase leading-[0.9] text-5xl md:text-7xl lg:text-8xl" data-testid="gallery-heading">
              The <span className="text-gold italic">Space</span>
            </h2>
          </div>
          <p className="max-w-sm text-zinc-400 text-base md:text-lg" data-testid="gallery-subcopy">
            A private, purpose-built training floor in the heart of Northwood. No queues for the rack. No distractions. Just you, Harry and the work.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 md:grid-flow-dense gap-4 md:gap-5" data-testid="gallery-grid">
          {galleryPhotos.map((p, i) => (
            <Tile key={p.id} photo={p} index={i} onOpen={openAt} />
          ))}
        </div>
      </div>

      <AnimatePresence>
        {open !== null && filled.length > 0 && (
          <Lightbox photos={filled} index={open} onClose={close} onStep={step} />
        )}
      </AnimatePresence>
    </section>
  );
}
